import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { RequestParamsType } from '@monsido/ng2/external/http/options';
import type { StatisticsSummary } from '@client/app/modules/statistics/interfaces/statistics-summary.interface';
import type { StatisticsRegion } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type {
    SiteAnalyticsBreadcrumbItem,
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';
import {
    buildLocationParentColumnAggregates,
    getCurrentPage,
    getNameLabel,
    getPerPage,
    getTotal,
    normalizeVisitorsRows,
} from '@client/app/feature-modules/site-analytics/definitions/visitors-definition.utils';

const VISITORS_LOCATION_REGIONS_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.visitors.locations.header',
    pageSubheaderKey: 'siteAnalytics.visitors.locations.regions.subheader',
    tableKey: 'site-analytics-visitors-location-regions',
    dataSource: 'visitorsLocationRegions',
    exportCategory: undefined,
    searchSupported: false,
    emptyMessageKey: 'siteAnalytics.visitors.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.visitors.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.visitors.locations.regions.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: '',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    comparisonEnabled: false,
    chart: {
        titleKey: 'siteAnalytics.visitors.locations.regions.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    breadcrumbsBuilder: (routeParams: Record<string, unknown>): SiteAnalyticsBreadcrumbItem[] => {
        const countryCode = toParamString(routeParams.countryCode);
        const countryName = toParamString(routeParams.countryName) || countryCode;

        return [
            {
                labelKey: 'siteAnalytics.visitors.locations.breadcrumb.all',
                state: 'base.customer.domain.statistics.visitors.locations.index',
            },
            {
                labelKey: 'siteAnalytics.visitors.locations.breadcrumb.country',
                state: 'base.customer.domain.statistics.visitors.locations.index',
            },
            {
                label: countryName,
            },
        ];
    },
    columns: [
        {
            id: 'name',
            headerKey: 'siteAnalytics.scaffold.table.column.name',
            displayType: 'link',
            width: 'xl',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'bounceRate',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRate',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'actionsPerVisit',
            headerKey: 'siteAnalytics.scaffold.table.column.actionsPerVisit',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            width: 'md',
        },
    ],
};

const loadVisitorsLocationRegionsData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;

    const countryCode = toParamString(request.routeParams?.countryCode);
    const countryName = toParamString(request.routeParams?.countryName) || countryCode;

    const params: RequestParamsType = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        search: request.search,
    });

    if (countryCode) {
        params.country_code = countryCode;
    }

    const [regionsResponse, parentLocationsResponse] = await Promise.all([
        statsRepoService.getRegions(params, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getLocations({
            domainId: request.domainId,
            from: params.from,
            to: params.to,
            search: countryName,
        }, contextTokens.NO_PARSE_SEARCH),
    ]);
    const rows = normalizeVisitorsRows(regionsResponse, (region) => {
        return createRegionLinkCell(region as StatisticsRegion, countryCode, countryName);
    });

    const parentSummary = Array.isArray(parentLocationsResponse) && parentLocationsResponse.length > 0
        ? parentLocationsResponse[0] as unknown as StatisticsSummary
        : null;

    return {
        rows,
        chart: {
            labels: rows.map((row) => getNameLabel(row)),
            primaryValues: rows.map((row) => toNumber(row.visits)),
        },
        summary: parentSummary,
        columnAggregates: buildLocationParentColumnAggregates(parentSummary),
        total: getTotal(regionsResponse, rows.length),
        perPage: getPerPage(regionsResponse, request.perPage),
        page: getCurrentPage(regionsResponse, request.page),
    };
};

function createRegionLinkCell (
    region: StatisticsRegion,
    countryCode: string,
    countryName: string,
): SiteAnalyticsLinkCell | string {
    const regionCode = String(region.region_code || '');
    const regionName = String(region.label || regionCode || '');

    if (!regionName) {
        return '-';
    }

    return {
        type: 'state',
        label: regionName,
        state: 'base.customer.domain.statistics.visitors.locations.cities',
        params: {
            countryCode,
            countryName,
            regionCode,
            regionName,
        },
    };
}

function toParamString (value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }

    return value;
}

function toNumber (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export const VISITORS_LOCATION_REGIONS_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'visitorsLocationRegions',
    config: VISITORS_LOCATION_REGIONS_CONFIG,
    loadData: loadVisitorsLocationRegionsData,
};

export const VISITORS_LOCATION_REGIONS_PAGE_CONFIG = VISITORS_LOCATION_REGIONS_DEFINITION.config;
